export interface AlignmentGuide {
  axis: 'horizontal' | 'vertical';
  position: number; // y for horizontal, x for vertical
}

export interface AlignmentSnapResult {
  snapDx: number;
  snapDy: number;
  guides: AlignmentGuide[];
}

export interface SnapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const SNAP_THRESHOLD = 10; // GRID_SIZE / 2

export function computeAlignmentSnap(
  draggedPositions: SnapRect[],
  staticNodes: SnapRect[],
): AlignmentSnapResult {
  if (draggedPositions.length === 0 || staticNodes.length === 0) {
    return { snapDx: 0, snapDy: 0, guides: [] };
  }

  // Collect all Y alignment points for static nodes
  const staticYs: number[] = [];
  for (const n of staticNodes) {
    staticYs.push(n.y);                        // top
    staticYs.push(n.y + n.height / 2);         // center-y
    staticYs.push(n.y + n.height);             // bottom
  }

  // Collect all X alignment points for static nodes
  const staticXs: number[] = [];
  for (const n of staticNodes) {
    staticXs.push(n.x);                        // left
    staticXs.push(n.x + n.width / 2);          // center-x
    staticXs.push(n.x + n.width);             // right
  }

  // Find best Y snap
  let bestDy = 0;
  let bestDyAbs = Infinity;
  for (const dp of draggedPositions) {
    const dragYs = [dp.y, dp.y + dp.height / 2, dp.y + dp.height];
    for (const dy of dragYs) {
      for (const sy of staticYs) {
        const diff = sy - dy;
        const absDiff = Math.abs(diff);
        if (absDiff <= SNAP_THRESHOLD && absDiff < bestDyAbs) {
          bestDyAbs = absDiff;
          bestDy = diff;
        }
      }
    }
  }

  // Find best X snap
  let bestDx = 0;
  let bestDxAbs = Infinity;
  for (const dp of draggedPositions) {
    const dragXs = [dp.x, dp.x + dp.width / 2, dp.x + dp.width];
    for (const dx of dragXs) {
      for (const sx of staticXs) {
        const diff = sx - dx;
        const absDiff = Math.abs(diff);
        if (absDiff <= SNAP_THRESHOLD && absDiff < bestDxAbs) {
          bestDxAbs = absDiff;
          bestDx = diff;
        }
      }
    }
  }

  // Apply snap and collect guides for exact matches
  const guides: AlignmentGuide[] = [];
  const seenH = new Set<number>();
  const seenV = new Set<number>();

  for (const dp of draggedPositions) {
    const snappedX = dp.x + bestDx;
    const snappedY = dp.y + bestDy;
    const dragYs = [snappedY, snappedY + dp.height / 2, snappedY + dp.height];
    const dragXs = [snappedX, snappedX + dp.width / 2, snappedX + dp.width];

    for (const dy of dragYs) {
      for (const sy of staticYs) {
        if (dy === sy && !seenH.has(sy)) {
          seenH.add(sy);
          guides.push({ axis: 'horizontal', position: sy });
        }
      }
    }

    for (const dx of dragXs) {
      for (const sx of staticXs) {
        if (dx === sx && !seenV.has(sx)) {
          seenV.add(sx);
          guides.push({ axis: 'vertical', position: sx });
        }
      }
    }
  }

  return { snapDx: bestDx, snapDy: bestDy, guides };
}
